import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-20 overflow-hidden flex items-center"
    >
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          poster="/videos/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
        >
          <source src="/videos/hero-best-of.webm" type="video/webm" />
          <source src="/videos/hero-best-of.mp4" type="video/mp4" />
        </video>
        {/* Overlay layers for readability (video still visible through) */}
        <div className="absolute inset-0 bg-[#FAF8F5]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5]/70 via-[#FAF8F5]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5]/95 via-[#FAF8F5]/15 to-transparent" />
      </div>

      <div className="container-xl w-full relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative z-10"
          >
            <div className="ornament mb-8">
              <span className="overline">Les Pennes-Mirabeau</span>
            </div>

            <h1 className="h1 text-5xl sm:text-6xl lg:text-[88px] mb-8 drop-shadow-[0_2px_12px_rgba(250,248,245,0.8)]">
              Votre sourire,
              <br />
              <span className="italic text-[#BE6C42] font-light">
                notre priorité.
              </span>
            </h1>

            <div className="max-w-xl mb-10">
              <div className="overline mb-3 !text-[#2D241E]/70">
                Un seul objectif
              </div>
              <p className="body-text text-lg sm:text-xl text-[#2D241E] font-light leading-relaxed">
                Vous proposer des soins de qualité,
                <br />
                dans un environnement{" "}
                <span className="italic text-[#BE6C42]">rassurant.</span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollTo("equipe")}
                className="btn-primary"
                data-testid="hero-cta-rdv"
              >
                Prendre rendez-vous
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => scrollTo("specialites")}
                className="btn-secondary"
                data-testid="hero-cta-explore"
              >
                Découvrir le cabinet
              </button>
            </div>

            <div className="mt-14 flex items-center gap-8 flex-wrap">
              <div className="flex items-center gap-2 text-[#2D241E]">
                <MapPin size={16} className="text-[#BE6C42]" />
                <span className="text-sm">
                  15 bis avenue François Mitterand
                </span>
              </div>
              <div className="h-4 w-px bg-[#2D241E]/20" />
              <a
                href="tel:0491515509"
                className="text-sm text-[#2D241E] hover:text-[#BE6C42] transition-colors"
                data-testid="hero-phone"
              >
                04 91 51 55 09
              </a>
            </div>
          </motion.div>

          {/* Floating badge on right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 hidden lg:flex justify-end relative z-10"
          >
            <div className="bg-white/85 backdrop-blur-xl rounded-2xl p-6 shadow-[0_20px_40px_rgba(45,36,30,0.15)] border border-[#E8DED5] w-[300px]">
              <div className="font-serif italic text-[#BE6C42] text-5xl leading-none">
                3
              </div>
              <div className="mt-3 text-sm text-[#2D241E] font-medium leading-snug">
                Praticiens experts à votre écoute, dans un cadre chaleureux.
              </div>
              <div className="mt-4 pt-4 border-t border-[#E8DED5] text-[11px] tracking-[0.22em] uppercase text-[#BE6C42] font-semibold">
                La Gavotte
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
